import { useEffect, useRef, type ReactNode } from 'react';
import { EMBLEM } from '../store/emblem.js';
import { beatLevel, useMusic } from '../store/music.js';

const COLOR = { w: '#f1f1f1', b: '#2f56c4', y: '#ffd23f' } as const;
const CELL_W = 4;
const CELL_H = 7;
const SCALE = 3; // спрайт малюємо втричі більшим за екранний: літери лишаються чіткими
const COUNT = 9;

/** Один раз малюємо емблему на окремому полотні: далі лише drawImage. */
function sprite(): HTMLCanvasElement {
  const rows = EMBLEM.chars.length;
  const cols = EMBLEM.chars[0]!.length;
  const cv = document.createElement('canvas');
  cv.width = cols * CELL_W * SCALE;
  cv.height = rows * CELL_H * SCALE;
  const ctx = cv.getContext('2d')!;
  ctx.scale(SCALE, SCALE);
  ctx.font = `bold ${CELL_H}px ui-monospace, Menlo, Consolas, monospace`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  for (let j = 0; j < rows; j++) {
    for (let i = 0; i < cols; i++) {
      const ch = EMBLEM.chars[j]![i]!;
      if (ch === ' ') continue;
      ctx.fillStyle = COLOR[EMBLEM.colors[j]![i] as keyof typeof COLOR] ?? '#fff';
      ctx.fillText(ch, i * CELL_W + CELL_W / 2, j * CELL_H + CELL_H / 2);
    }
  }
  return cv;
}

interface Flyer {
  x: number;
  y: number;
  size: number;
  vx: number;
  vy: number;
  spin: number;
  angle: number;
  phase: number;
  alpha: number;
}

/**
 * Зграя одна на весь сайт. Тло малюється двома полотнами — під шапкою й під
 * сторінкою налаштувань, — і коли в кожного були свої емблеми, шапка й сторінка
 * виглядали як різні шматки. Тепер обидва полотна малюють ті самі емблеми в
 * тих самих місцях: рух рахується раз на кадр, хто б його не попросив.
 */
const flock = {
  flyers: [] as Flyer[],
  img: null as HTMLCanvasElement | null,
  /** Мітка кадру rAF: у межах одного кадру всі полотна отримують ту саму. */
  at: 0,
  users: 0,
};

const spriteOnce = (): HTMLCanvasElement => (flock.img ??= sprite());

const spawn = (w: number, h: number, fromEdge: boolean): Flyer => {
  const img = spriteOnce();
  const size = 0.35 + Math.random() * 0.45;
  return {
    x: Math.random() * w,
    y: fromEdge ? h + (img.height / SCALE) * size : Math.random() * h,
    size,
    vx: (Math.random() - 0.5) * 30,
    vy: -(25 + Math.random() * 55),
    spin: (Math.random() - 0.5) * 0.5,
    angle: (Math.random() - 0.5) * 0.6,
    phase: Math.random() * 6.28,
    alpha: 0.35 + Math.random() * 0.4,
  };
};

/** Крок зграї — лише раз за кадр, скільки б полотен не малювало. */
function advance(now: number, w: number, h: number): void {
  if (!flock.flyers.length) {
    flock.flyers = Array.from({ length: COUNT }, () => spawn(w, h, false));
    flock.at = now;
    return;
  }
  if (now === flock.at) return;
  const dt = Math.min(0.05, Math.max(0, (now - flock.at) / 1000));
  flock.at = now;
  const img = spriteOnce();
  for (let k = 0; k < flock.flyers.length; k++) {
    const f = flock.flyers[k]!;
    f.x += (f.vx + Math.sin(now / 900 + f.phase) * 18) * dt;
    f.y += f.vy * dt;
    f.angle += f.spin * dt;
    if (f.y < -(img.height / SCALE) * f.size) flock.flyers[k] = spawn(w, h, true);
  }
}

/**
 * Емблеми Держспецзв’язку з символів: коли грає гімн, летять тлом, повільно
 * обертаються й пульсують у такт. Поза музикою полотна немає зовсім.
 */
export function EmblemFly(): ReactNode {
  const playing = useMusic(s => s.playing);
  const ref = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const cv = ref.current;
    const ctx = cv?.getContext('2d');
    if (!playing || !cv || !ctx || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const img = spriteOnce();
    flock.users += 1;
    let w = 0;
    let h = 0;
    const resize = (): void => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      w = window.innerWidth;
      h = window.innerHeight;
      cv.width = Math.round(w * dpr);
      cv.height = Math.round(h * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();

    let raf = 0;
    const frame = (now: number): void => {
      raf = requestAnimationFrame(frame);
      advance(now, w, h);
      ctx.clearRect(0, 0, w, h);
      const pulse = 1 + beatLevel * 0.28;
      for (const f of flock.flyers) {
        const dw = (img.width / SCALE) * f.size * pulse;
        const dh = (img.height / SCALE) * f.size * pulse;
        ctx.save();
        ctx.translate(f.x, f.y);
        ctx.rotate(f.angle);
        ctx.globalAlpha = Math.min(1, f.alpha + beatLevel * 0.35);
        ctx.drawImage(img, -dw / 2, -dh / 2, dw, dh);
        ctx.restore();
      }
    };
    raf = requestAnimationFrame(frame);
    window.addEventListener('resize', resize);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', resize);
      // Останнє полотно пішло — наступного разу зграя народиться наново.
      flock.users -= 1;
      if (!flock.users) flock.flyers = [];
    };
  }, [playing]);

  if (!playing) return null;
  return <canvas ref={ref} className="ambient-ascii ambient-emblems" />;
}
